'use client';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import { queryKeys } from '../lib/queryClient';
import { literalField, rawField } from '../lib/jsonBody';

const getCaseBattlesChannel = (prefix: string): string =>
  prefix ? `${prefix}_case_battles` : 'case_battles';

const getCaseBattleLiveChannel = (prefix: string): string =>
  prefix ? `${prefix}_case_battle_live` : 'case_battle_live';

const CASE_BATTLE_EVENT_TYPES = [
  'battle_created',
  'player_joined',
  'player_left',
  'battle_started',
  'battle_completed',
  'battle_cancelled',
  'vote',
  'reaction',
  'viewers',
  'rematch',
] as const;

export type CaseBattleEventType = (typeof CASE_BATTLE_EVENT_TYPES)[number];

export interface CaseBattleEvent {
  type: CaseBattleEventType;
  data: {
    battleId?: string;
    userId?: string;
    slot?: number;
    winnerId?: string;
    isTie?: boolean;
    winnerTeam?: number;
    startNow?: boolean;
    emoji?: string;
    participant?: boolean;
    viewers?: number;
    rematchBattleId?: string;
    reason?: string;
    [key: string]: unknown;
  };
}

const isCaseBattleEvent = (value: unknown): value is CaseBattleEvent => {
  const data = rawField(value, 'data');
  return (
    literalField(value, 'type', CASE_BATTLE_EVENT_TYPES) !== undefined &&
    typeof data === 'object' &&
    data !== null
  );
};

interface UseCaseBattleSubscriptionOptions {
  onEvent?: (event: CaseBattleEvent) => void;
}

const LOBBY_REFRESH_DEBOUNCE_MS = 400;

const invalidateLobby = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['caseBattles', 'lobby'] });
  queryClient.invalidateQueries({ queryKey: queryKeys.userActiveBattlesCount });
};

const invalidateHistory = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === 'caseBattles' && key[1] === 'history';
    },
  });

export const useCaseBattleSubscription = (
  battleId: string | undefined,
  options?: UseCaseBattleSubscriptionOptions
) => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const onEventRef = useRef(options?.onEvent);

  useEffect(() => {
    onEventRef.current = options?.onEvent;
  }, [options?.onEvent]);

  const channels = useMemo(
    () =>
      battleId
        ? [getCaseBattlesChannel(channelPrefix), getCaseBattleLiveChannel(channelPrefix)]
        : [getCaseBattlesChannel(channelPrefix)],
    [channelPrefix, battleId],
  );

  const lobbyTimer = useRef<number | null>(null);
  const refreshLobbySoon = useCallback(() => {
    if (lobbyTimer.current !== null) return;
    lobbyTimer.current = window.setTimeout(() => {
      lobbyTimer.current = null;
      invalidateLobby(queryClient);
    }, LOBBY_REFRESH_DEBOUNCE_MS);
  }, [queryClient]);
  useEffect(
    () => () => {
      if (lobbyTimer.current !== null) window.clearTimeout(lobbyTimer.current);
    },
    [],
  );

  const handleEvent = useCallback(
    (data: unknown) => {
      if (!isCaseBattleEvent(data)) {
        console.warn('[CaseBattle] Received malformed event:', data);
        return;
      }
      const event = data;

      if (battleId && event.data.battleId !== battleId) {
        return;
      }

      onEventRef.current?.(event);

      const details = battleId ? queryKeys.caseBattleDetails(battleId) : null;

      switch (event.type) {
        case 'battle_created':
          refreshLobbySoon();
          break;

        case 'player_joined':
        case 'player_left':
        case 'battle_started':
          if (details) queryClient.invalidateQueries({ queryKey: details });
          refreshLobbySoon();
          break;

        case 'battle_completed':
          if (details) {
            queryClient.prefetchQuery({ queryKey: details });
            queryClient.invalidateQueries({ queryKey: details });
          }
          refreshLobbySoon();
          queryClient.invalidateQueries({ queryKey: queryKeys.profile });
          invalidateHistory(queryClient);
          break;

        case 'battle_cancelled':
          if (details) queryClient.invalidateQueries({ queryKey: details });
          refreshLobbySoon();
          queryClient.invalidateQueries({ queryKey: queryKeys.profile });
          break;

        case 'vote':
        case 'rematch':
          if (details) queryClient.invalidateQueries({ queryKey: details });
          break;

        case 'reaction':
        case 'viewers':
          break;
      }
    },
    [battleId, queryClient, refreshLobbySoon],
  );

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribes = channels.map((channel) => subscribe(channel, handleEvent));

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [isConnected, channels, subscribe, handleEvent]);

  return { isConnected };
};
